import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { router } from '@/router';
import { PostHogProvider } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import '@/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Get the root element and assert it exists
const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// PostHog configuration - only initialize in production
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
const isProduction = import.meta.env.PROD;

const posthogOptions = {
    api_host: posthogHost,
    capture_pageview: false, // We'll handle this manually for SPA
    autocapture: true,
    capture_pageleave: true,
} as const;

// Create root and render app
ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
            <HelmetProvider>
                {isProduction && posthogKey ? (
                    <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
                        <RouterProvider router={router} />
                    </PostHogProvider>
                ) : (
                    <RouterProvider router={router} />
                )}
            </HelmetProvider>
        </QueryClientProvider>
    </React.StrictMode>
);
